import * as Zod from "zod";
import { IdentityClaims } from "./tokens";

const flagDecoders = {
  "experiment-price-estimate-email-screen": Zod.union([
    Zod.literal("unallocated"),
    Zod.literal("control"),
    Zod.literal("variant 1"),
  ]),
  "experiment-price-check-info-screen": Zod.union([
    Zod.literal("unallocated"),
    Zod.literal("control"),
    Zod.literal("variant"),
  ]),

  "experiment-nav-updates": Zod.union([
    Zod.literal("unallocated"),
    Zod.literal("control"),
    Zod.literal("variant 1"),
  ]),

  "experiment-value-prop-messaging": Zod.union([
    Zod.literal("control"),
    Zod.literal("variant 1"),
    Zod.literal("variant 2"),
    Zod.literal("variant 3"),
  ]),

  "experiment-compare-drop-down-behind-login": Zod.union([
    Zod.literal("unallocated"),
    Zod.literal("control"),
    Zod.literal("enabled"),
  ]),

  "experiment-checkout-flow-buyer-trust": Zod.union([
    Zod.literal("control"),
    Zod.literal("variant: trust building content"),
  ]),

  "mpo-submit-new-api": Zod.boolean(),
  "chrome-extension-header-link": Zod.boolean(),
  "mpc-submit-new-api": Zod.boolean(),
  "disable-pending-request-check": Zod.boolean(),
  "mpc-ai-summary": Zod.boolean(),
  "share-price-checks": Zod.boolean(),
  "fullscreen-price-checks": Zod.boolean(),
  "price-estimate-preview-redesign": Zod.boolean(),
  "rootly-buy-with-aws-demo": Zod.boolean(),
  "feature-update-price-estimates": Zod.boolean(),
  "cart-poc": Zod.boolean(),
  "drata-buy-now": Zod.boolean(),
  "rootly-buy-now": Zod.boolean(),
  "suger-buy-now": Zod.boolean(),
  "astronomer-buy-now": Zod.boolean(),
  "snyk-buy-now": Zod.boolean(),
  "zoominfo-buy-now": Zod.boolean(),
  "use-real-recent-savings": Zod.boolean(),
  "custom-listing-sections": Zod.boolean(),
  "custom-offer-buy-with-aws": Zod.boolean(),
  "new-landing-page": Zod.boolean(),
  "new-landing-page-links": Zod.boolean(),
  "homepage-updates": Zod.boolean(),
  "homepage-chat": Zod.boolean(),
  "clerk-auth": Zod.boolean(),
  "seller-portal": Zod.boolean(),
  "company-page-delta": Zod.boolean(),
  "pricing-api-homepage": Zod.boolean(),
};

export type FlagName = keyof typeof flagDecoders;

export type Flags = {
  [Property in FlagName]: Zod.infer<(typeof flagDecoders)[Property]>;
};

export const isKnownFlag = (flag: string): flag is FlagName => {
  if (flagDecoders[flag as FlagName]) {
    return true;
  } else {
    return false;
  }
};

export const isExperiment = (flag: FlagName): boolean => {
  // We can do whatever here, but I figured we could try a convention for naming experiments to make it easier.
  return flag.startsWith("experiment-");
};

export const isKnownExperiment = (flag: string): boolean => {
  return isKnownFlag(flag) && isExperiment(flag);
};

export const decodeFlag = <T extends FlagName>(
  flag: T,
  value: unknown,
): Flags[T] | null => {
  const zodDecoder = flagDecoders[flag];

  const zodResult = zodDecoder.safeParse(value);

  if (zodResult.success) {
    return zodResult.data as Flags[T];
  } else {
    return null;
  }
};

export type Context = { user: Record<string, number | string | boolean> };

export const makeContext = (args: {
  identity?: IdentityClaims;
  anonymousId?: string;
}): Context => {
  if (args.identity) {
    const organizationId = args.identity.organizationId;
    const domain = args.identity.domain;
    const context: Context = {
      user: {
        ...(organizationId ? { organizationId } : {}),
        ...(domain ? { domain } : {}),
        email: args.identity.userEmail,
      },
    };

    if (args.anonymousId) {
      context.user.anonymousId = args.anonymousId;
    }

    const trackingId =
      args.identity.trackingId ?? args.anonymousId ?? args.identity.userId;

    if (trackingId) {
      context.user.trackingId = trackingId;
      context.user.key = trackingId;
    }

    if (args.identity.userId) {
      context.user.id = args.identity.userId;
    }

    if (args.identity.personId) {
      context.user.personId = args.identity.personId;
    }

    if (args.identity.role) {
      context.user.role = args.identity.role;
    }

    return context;
  } else {
    const context: Context = { user: {} };

    if (args.anonymousId) {
      context.user.anonymousId = args.anonymousId;
      context.user.trackingId = args.anonymousId;
      context.user.key = args.anonymousId;
    }

    return context;
  }
};
